import { NgComponentOutlet } from '@angular/common';
import {
  afterRenderEffect,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  Injector,
  signal,
  viewChild,
  ViewContainerRef,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { JsonFormsModule } from '@jsonforms/angular';
import { L10N_LOCALE, L10nTranslatePipe } from 'angular-l10n';
import { angularMaterialRenderers } from '@jsonforms/angular-material';
import { LogHelper } from '../../logging/log-helper';
import { createDashboardWidgetPort, widgetInjector } from './dashboard-widget-handle';
import { hasSettings, sanitizeSettings, settingsModeOf } from './dashboard-widget-settings';
import { lazyComponent } from '../frame/lazy-component';
import { DashboardWidgetInstance } from '../layout/dashboard-layout';
import { DashboardSettingsComponent, DashboardWidgetDefinition } from './dashboard-widget-definition';

export interface DashboardWidgetSettingsData {
  definition: DashboardWidgetDefinition;
  instance: DashboardWidgetInstance;
}

const FOCUSABLE_FIELD = 'input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex="0"]';

function isDashboardSettingsComponent(value: unknown): value is DashboardSettingsComponent {
  return typeof (value as Partial<DashboardSettingsComponent> | null)?.collectSettings === 'function';
}

@Component({
  selector: 'app-dashboard-widget-settings-dialog',
  templateUrl: './dashboard-widget-settings-dialog.component.html',
  styleUrls: ['./dashboard-widget-settings-dialog.component.scss'],
  imports: [
    MatDialogModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    NgComponentOutlet,
    JsonFormsModule,
    L10nTranslatePipe,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DashboardWidgetSettingsDialogComponent {
  protected readonly data = inject<DashboardWidgetSettingsData>(MAT_DIALOG_DATA);
  protected readonly locale = inject(L10N_LOCALE);
  private readonly dialogRef = inject<MatDialogRef<DashboardWidgetSettingsDialogComponent, unknown>>(MatDialogRef);

  private readonly componentOutlet = viewChild(NgComponentOutlet);
  private readonly customSettingsHost = viewChild<ElementRef<HTMLElement>>('customSettingsHost');

  protected readonly mode = settingsModeOf(this.data.definition) === 'component' ? 'component' : 'schema';
  private readonly port = createDashboardWidgetPort(this.data.definition, signal(this.data.instance), signal(true));
  protected readonly settingsInjector = widgetInjector(this.port, inject(Injector));

  protected readonly settingsComponent = lazyComponent(() => {
    const { type, loadSettingsComponent } = this.data.definition;
    return loadSettingsComponent && { load: loadSettingsComponent, label: `settings for widget type '${type}'` };
  });

  protected readonly renderers = angularMaterialRenderers;
  protected readonly initialSettings: unknown = this.port.settings() ?? {};
  private readonly formData = signal<unknown>(this.initialSettings);
  private readonly hasErrors = signal(false);
  /** The last Save gave settings the widget type rejects */
  protected readonly saveFailed = signal(false);

  /** The loaded settings component, once it has rendered; null when it doesn't implement the contract */
  private readonly customSettings = computed(() => {
    const instance: unknown = this.componentOutlet()?.componentInstance;
    return isDashboardSettingsComponent(instance) ? instance : null;
  });
  protected readonly brokenComponent = computed(() => !!this.componentOutlet() && !this.customSettings());
  protected readonly canSave = computed(() =>
    this.mode === 'component' ? !!this.customSettings() : !this.hasErrors(),
  );

  /**
   * Closes with valid new settings, or undefined when cancelled; null for a widget without settings. Opens in the
   * dashboard's `viewContainerRef`, so a custom settings component can use the dashboard state.
   */
  static open(
    dialog: MatDialog,
    data: DashboardWidgetSettingsData,
    viewContainerRef: ViewContainerRef,
  ): MatDialogRef<DashboardWidgetSettingsDialogComponent, unknown> | null {
    if (!hasSettings(data.definition)) {
      return null;
    }

    return dialog.open<DashboardWidgetSettingsDialogComponent, DashboardWidgetSettingsData, unknown>(
      DashboardWidgetSettingsDialogComponent,
      { data, viewContainerRef, width: '560px', maxWidth: '95vw', autoFocus: 'first-tabbable' },
    );
  }

  constructor() {
    effect(() => {
      if (this.brokenComponent()) {
        console.error(
          `Settings component for widget type '${this.data.definition.type}' must implement DashboardSettingsComponent`,
        );
      }
    });
    afterRenderEffect(() => {
      if (this.settingsComponent.state().status === 'ready') {
        this.customSettingsHost()?.nativeElement.querySelector<HTMLElement>(FOCUSABLE_FIELD)?.focus();
      }
    });
  }

  protected onDataChange(data: unknown): void {
    this.formData.set(data);
    this.saveFailed.set(false);
  }

  protected onErrors(errors: unknown[]): void {
    this.hasErrors.set(errors.length > 0);
  }

  /** Validates before closing, so a rejected edit isn't lost with the dialog */
  protected save(): void {
    if (!this.canSave()) {
      return;
    }

    const settings = this.collect();
    if (settings === undefined || sanitizeSettings(this.data.definition, settings).invalid) {
      this.saveFailed.set(true);
      return;
    }

    this.dialogRef.close(settings);
  }

  protected cancel(): void {
    this.dialogRef.close();
  }

  /** Undefined when the settings component fails to give any */
  private collect(): unknown {
    if (this.mode === 'schema') {
      return this.formData();
    }

    try {
      return this.customSettings()?.collectSettings();
    } catch (error) {
      LogHelper.error(
        'DashboardWidgetSettings',
        `Settings component for widget type '${this.data.definition.type}' failed to collect settings`,
        error,
      );
      return undefined;
    }
  }
}
